import React, { useState } from 'react';
import { RatingBar } from '@ubeya/shared-web/components';
import { EMPLOYEE_CLIENT_PREFERENCE_STATUS } from '@ubeya/shared-web/utils/employee';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import { BlockEmployeeOnRatingModal, MarkPreferredOnRatingModal } from './CellRenderers';

// This component supports rating for both internal and external employees
const ExpandedRatingRenderer = ({
  rowData,
  columnData: {
    onChangeRating,
    onExternalChangeRating,
    onEmployeeStatusChange,
    readOnly = false,
    hideZeroRating = false,
    mappedSupplierBranches
  }
}) => {
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showMarkPreferredModal, setShowMarkPreferredModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { summarized, subRows, isJob, rating, isExternal, clientId, supplierData, isThirdParty } = rowData;

  const preferOrBlockModalHandler = (newRating, employeeClientStatus) => {
    // The employee is from direct supplier (not 3rd party) and the current account is the most left client in the project
    const isClientSupplierDirectConnection = !clientId && !isThirdParty && isExternal;

    // The employee is from supplier account(the current account) and is part of an order
    const isSupplierEmployeeInOrder = !!clientId && !isExternal;

    const enableRatingModals = isClientSupplierDirectConnection || isSupplierEmployeeInOrder;
    if (!enableRatingModals) return;

    if (newRating === 1 && employeeClientStatus !== EMPLOYEE_CLIENT_PREFERENCE_STATUS.BLOCKED) {
      const isClientAllowedToBlockSupplierEmployees =
        mappedSupplierBranches?.[supplierData?.branchId]?.config?.allowClientToBlockEmployees;

      if (isClientSupplierDirectConnection && !isClientAllowedToBlockSupplierEmployees) return;

      setShowBlockModal(true);
      return;
    }
    if (newRating === 5 && employeeClientStatus !== EMPLOYEE_CLIENT_PREFERENCE_STATUS.PREFERRED) {
      setShowMarkPreferredModal(true);
    }
  };

  const ratingChangeHandler = async (newRating) => {
    setIsLoading(true);
    try {
      const actualValue = newRating === rating ? null : newRating;
      const changeRatingOutput = await (isExternal
        ? onExternalChangeRating?.({ ...rowData, slotId: rowData?.id, value: actualValue })
        : onChangeRating?.({ ...rowData, value: actualValue }));

      const employeeClientStatus = changeRatingOutput?.data?.employeesClientStatus;
      const employeeId = rowData?.employee?.id;
      if (employeeId) {
        trackEvent('Star rating changed', {
          employee_id: employeeId,
          star_rating: actualValue,
          module: 'Timesheet'
        });
      }
      if (newRating !== rating) {
        preferOrBlockModalHandler(newRating, employeeClientStatus);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (summarized || subRows || isJob) {
    return <></>;
  }

  return (
    <>
      <RatingBar
        isLocked={readOnly || isLoading}
        center={false}
        rating={rating}
        hideZeroRating={hideZeroRating}
        onChange={ratingChangeHandler}
      />
      {showBlockModal && (
        <BlockEmployeeOnRatingModal
          data={rowData}
          onClose={() => setShowBlockModal(false)}
          onEmployeeStatusChange={onEmployeeStatusChange}
          isClient={!rowData?.clientId && !rowData?.clientBranch?.accountId}
        />
      )}
      {showMarkPreferredModal && (
        <MarkPreferredOnRatingModal
          data={rowData}
          onClose={() => setShowMarkPreferredModal(false)}
          onEmployeeStatusChange={onEmployeeStatusChange}
          isClient={!rowData?.clientId && !rowData?.clientBranch?.accountId}
        />
      )}
    </>
  );
};

export default ExpandedRatingRenderer;
